import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Checkbox, EmptyState, Icon, Input, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../../lib/api.js";
import type { Channel } from "../../lib/api.js";
import { useAuth } from "../../lib/auth.js";
import type { SearchablePerson } from "./types.js";

/* ──────────────────────────────── channel browser ──────────────────────────────── */

export type BrowsedChannel = Pick<Channel, "id" | "name" | "topic" | "kind" | "archivedAt"> & {
  memberCount: number;
  isMember: boolean;
  canManage: boolean;
};

/**
 * Every public channel in the office, searchable, with who is in it and what
 * it is for. Joining and leaving happen here without opening the channel.
 */
export function ChannelBrowserDialog({
  onOpen,
  onChanged,
}: {
  /** Open a channel the viewer is already in (or has just joined). */
  onOpen: (channelId: string) => void;
  /** Membership or archive state changed: the sidebar should refetch. */
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [items, setItems] = useState<BrowsedChannel[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Guards against a slow earlier search landing after a newer one.
  const requestRef = useRef(0);

  const load = useCallback(
    (after: string | null) => {
      const request = ++requestRef.current;
      setLoading(true);
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (includeArchived) params.set("includeArchived", "true");
      if (after) params.set("after", after);
      api
        .get<{ channels: BrowsedChannel[]; hasMore: boolean }>(`/channels/browse?${params}`)
        .then((res) => {
          if (request !== requestRef.current) return;
          setItems((prev) => (after ? [...prev, ...res.channels] : res.channels));
          setCursor(res.hasMore && res.channels.length ? res.channels[res.channels.length - 1]!.id : null);
        })
        .catch((error: unknown) => {
          if (request === requestRef.current)
            toast(error instanceof ApiError ? error.message : "Could not load channels.", { tone: "danger" });
        })
        .finally(() => request === requestRef.current && setLoading(false));
    },
    [query, includeArchived, toast],
  );

  // Debounced so typing a name is one request, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => load(null), 200);
    return () => clearTimeout(timer);
  }, [load]);

  const patch = (id: string, changes: Partial<BrowsedChannel>) =>
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, ...changes } : c)));

  const join = async (channel: BrowsedChannel) => {
    setBusyId(channel.id);
    try {
      await api.post(`/channels/${channel.id}/join`);
      patch(channel.id, { isMember: true, memberCount: channel.memberCount + 1 });
      onChanged();
      onOpen(channel.id);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not join that channel.", { tone: "danger" });
    } finally {
      setBusyId(null);
    }
  };

  const leave = async (channel: BrowsedChannel, userId: string) => {
    setBusyId(channel.id);
    try {
      await api.delete(`/channels/${channel.id}/members/${userId}`);
      patch(channel.id, { isMember: false, memberCount: Math.max(0, channel.memberCount - 1) });
      onChanged();
      toast(`Left #${channel.name}.`);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not leave that channel.", { tone: "danger" });
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (channel: BrowsedChannel, archiving: boolean) => {
    setBusyId(channel.id);
    try {
      await api.post(`/channels/${channel.id}/${archiving ? "archive" : "unarchive"}`);
      patch(channel.id, { archivedAt: archiving ? new Date().toISOString() : null });
      onChanged();
      toast(archiving ? `Archived #${channel.name}.` : `Restored #${channel.name}.`);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not change that channel.", { tone: "danger" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="chat-browser">
      <Input
        placeholder="Search channels by name or topic"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      <label className="chat-browser__archived">
        <Checkbox checked={includeArchived} onChange={() => setIncludeArchived((v) => !v)} aria-label="Show archived" />
        Show archived channels
      </label>

      <div className="chat-browser__list">
        {items.length === 0 && loading ? (
          <SkeletonText lines={5} />
        ) : items.length === 0 ? (
          <EmptyState icon="chat" title="No channels found" description="Try a different search, or start a new channel." />
        ) : (
          items.map((channel) => (
            <ChannelRow
              key={channel.id}
              channel={channel}
              busy={busyId === channel.id}
              onOpen={() => {
                onOpen(channel.id);
              }}
              onJoin={() => void join(channel)}
              onLeave={(userId) => void leave(channel, userId)}
              onArchive={(archiving) => void archive(channel, archiving)}
            />
          ))
        )}
      </div>

      {cursor && (
        <Button variant="ghost" size="sm" loading={loading} onClick={() => load(cursor)}>
          Show more
        </Button>
      )}
    </div>
  );
}

function ChannelRow({
  channel,
  busy,
  onOpen,
  onJoin,
  onLeave,
  onArchive,
}: {
  channel: BrowsedChannel;
  busy: boolean;
  onOpen: () => void;
  onJoin: () => void;
  onLeave: (userId: string) => void;
  onArchive: (archiving: boolean) => void;
}) {
  const { viewer } = useAuth();
  const archived = !!channel.archivedAt;
  return (
    <div className={`chat-browser__row ${archived ? "is-archived" : ""}`}>
      <div className="chat-browser__text">
        <strong>
          <span aria-hidden="true">#</span> {channel.name}
          {archived && <span className="chat-browser__tag">Archived</span>}
        </strong>
        <span className="chat-browser__meta">
          {channel.memberCount} {channel.memberCount === 1 ? "member" : "members"}
          {channel.topic ? ` · ${channel.topic}` : ""}
        </span>
      </div>
      <div className="chat-browser__actions">
        {channel.isMember ? (
          <>
            <Button size="sm" variant="secondary" onClick={onOpen}>
              Open
            </Button>
            {!archived && viewer && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onLeave(viewer.id)}>
                Leave
              </Button>
            )}
          </>
        ) : (
          !archived && (
            <Button size="sm" loading={busy} onClick={onJoin}>
              Join
            </Button>
          )
        )}
        {channel.canManage && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onArchive(!archived)}>
            {archived ? "Restore" : "Archive"}
          </Button>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────── new group message ──────────────────────────────── */

/** The most other people a group message can hold; the API enforces the same. */
export const GROUP_MIN_OTHERS = 2;
export const GROUP_MAX_OTHERS = 8;

/**
 * Pick 2–8 people to message together. If the same set already has a group,
 * the server returns that one instead of making a second.
 */
export function NewGroupDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (channel: Channel) => void;
}) {
  const { toast } = useToast();
  const { viewer } = useAuth();
  const [people, setPeople] = useState<SearchablePerson[]>([]);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ people: SearchablePerson[] }>("/people")
      .then((res) => !cancelled && setPeople(res.people.filter((p) => p.id !== viewer?.id)))
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load people.", { tone: "danger" });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [toast, viewer?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) => [p.displayName, p.jobTitle, p.department].some((f) => f?.toLowerCase().includes(q)));
  }, [people, query]);

  const toggle = (id: string) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < GROUP_MAX_OTHERS) next.add(id);
      return next;
    });

  const submit = () => {
    setSaving(true);
    api
      .post<{ channel: Channel }>("/channels/group", { userIds: [...chosen] })
      .then((res) => {
        onCreated(res.channel);
        onClose();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not start that group.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  };

  const atMax = chosen.size >= GROUP_MAX_OTHERS;

  return (
    <div className="chat-add-members">
      <Input placeholder="Search by name, title or team" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
      <p className="chat-browser__meta">
        Choose {GROUP_MIN_OTHERS} to {GROUP_MAX_OTHERS} people. {chosen.size} chosen.
        {atMax ? " That is the most a group message holds." : ""}
      </p>
      <div className="chat-add-members__list">
        {loading ? (
          <SkeletonText lines={5} />
        ) : filtered.length === 0 ? (
          <p className="chat-add-members__empty">Nobody matches.</p>
        ) : (
          filtered.map((person) => {
            const picked = chosen.has(person.id);
            return (
              <label key={person.id} className={`chat-add-members__row ${!picked && atMax ? "is-member" : ""}`}>
                <Checkbox
                  checked={picked}
                  disabled={!picked && atMax}
                  onChange={() => toggle(person.id)}
                  aria-label={`Include ${person.displayName}`}
                />
                <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={26} />
                <span className="chat-add-members__name">
                  <strong>{person.displayName}</strong>
                  <span>{person.jobTitle ?? person.department ?? ""}</span>
                </span>
              </label>
            );
          })
        )}
      </div>
      <div className="chat-add-members__actions">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          disabled={chosen.size < GROUP_MIN_OTHERS || saving}
          loading={saving}
          icon={<Icon name="people" size={14} />}
          onClick={submit}
        >
          Start group
        </Button>
      </div>
    </div>
  );
}
